<script setup>
import { computed, ref, watchEffect } from 'vue'
import googleSignInImage from '@/assets/google-sign-in.png'
import InquiryList from '@/components/InquiryList.vue'
import DualInputSlider from '@/components/DualInputSlider.vue'
import DetailedResult from '../components/DetailedResult.vue'

const settings = window.drupalSettings?.gcSpa ?? {}
const used = ref(Number(settings.used) || 0)
const limit = Number(settings.limit) || 3
const authenticated = Boolean(settings.authenticated)
const loginUrl = settings.loginUrl || '/user/login/google'
const loginRequired = computed(() => !authenticated && used.value > limit)
const saving = ref(false)
const loginDialog = ref(null)

function openLoginDialog() {
  if (loginDialog.value && !loginDialog.value.open) loginDialog.value.showModal()
}

watchEffect(() => {
  if (loginRequired.value) openLoginDialog()
  else if (loginDialog.value?.open) loginDialog.value.close()
})

const calcHistory = ref(Array.isArray(settings.history) ? settings.history : [])
const conversionType = ref('eag_to_a1c')
const targetUnit = ref('mg_dl')
const inputValue = ref(180)
const calculatedResult = computed(() => {
  const value = parseFloat(inputValue.value)
  let result = ''
  if (isNaN(value)) {
    return 'Please enter a valid number.'
  }

  if (conversionType.value === 'a1c_to_eag') {
    // A1C -> eAG: eAG = 28.7 * A1C - 46.7 (mg/dL)
    const eagMgDl = (28.7 * value) - 46.7
    result = targetUnit.value === 'mg_dl' ? Math.round(eagMgDl.toFixed(1)) : parseFloat((eagMgDl / 18).toFixed(1))
  }
  else {
    // eAG -> A1C: A1C = (eAG + 46.7) / 28.7 (mg/dL)
    const eagMgDl = targetUnit.value === 'mg_dl' ? value : value * 18
    result = parseFloat(((eagMgDl + 46.7) / 28.7).toFixed(1))
  }

  return result
})

const inputError = computed(() =>
  typeof calculatedResult.value === 'string' ? calculatedResult.value : ''
)

async function updateHistory() {
  if (saving.value || loginRequired.value || !Number.isFinite(calculatedResult.value)) {
    return
  }

  saving.value = true

  try {
    const entry = {
      conversionType: conversionType.value,
      targetUnit: targetUnit.value,
      inputValue: inputValue.value,
      calculatedResult: calculatedResult.value
    }

    const response = await fetch('/inquiry/add', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify(entry)
    })

    if (response.status === 403) {
      used.value = Number(response.headers.get('X-GC-Calculation-Count')) || limit + 1
      return
    }
    if (!response.ok) {
      return
    }

    calcHistory.value.unshift(entry)
    if (!authenticated) {
      used.value = Number(response.headers.get('X-GC-Calculation-Count')) || used.value + 1
    }
  }
  finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="main-content">
    <Teleport to="body">
      <dialog ref="loginDialog" class="login-required" aria-labelledby="login-required-title" aria-describedby="login-required-description" @cancel.prevent>
        <h2 id="login-required-title">Log in to continue</h2>
        <p id="login-required-description">Create an account, view your historical data and enjoy the FREE perks.</p>
        <a class="google-sign-in-link" :href="loginUrl" autofocus>
          <img :src="googleSignInImage" alt="Sign in with Google" />
        </a>
      </dialog>
    </Teleport>
    <form class="form-container" @submit.prevent="updateHistory">
      <div class="form-field-layout">
        <DualInputSlider
          v-if="conversionType === 'eag_to_a1c' && targetUnit === 'mg_dl'"
          hint="Your eAG value in mg/dL"
          :min="1"
          :max="300"
          :step="1"
          :error="inputError"
          :disabled="loginRequired"
          v-model="inputValue"
        />
        <DualInputSlider
          v-if="conversionType === 'eag_to_a1c' && targetUnit === 'mmol_l'"
          hint="Your eAG value in mmol/L"
          :min="1"
          :max="25"
          :step="0.1"
          :error="inputError"
          :disabled="loginRequired"
          v-model="inputValue"
        />
        <DualInputSlider
          v-if="conversionType === 'a1c_to_eag'"
          hint="Your A1C value in percent"
          :min="1.6"
          :max="15"
          :step="0.1"
          :error="inputError"
          :disabled="loginRequired"
          v-model="inputValue"
        />
      </div>

      <div class="form-fieldgroup conversion-options">
        <div class="form-field-layout with-label">
          <div class="field-label">Conversion</div>

          <div class="form-field-element">
            <div class="choice-container">
              <div role="radiogroup" class="choice-options">
                <label class="choice-option">
                  <input
                    type="radio"
                    name="conversion-type"
                    class="choice-input"
                    value="eag_to_a1c"
                    v-model="conversionType"
                  />

                  <div class="choice-option-container">
                    <div class="choice-radio"></div>
                    <div class="choice-overlay"></div>
                  </div>
                  <div class="choice-label-text">eAG to A1C</div>
                </label>

                <label class="choice-option">
                  <input
                    type="radio"
                    name="conversion-type"
                    class="choice-input"
                    value="a1c_to_eag"
                    v-model="conversionType"
                  />

                  <div class="choice-option-container">
                    <div class="choice-radio"></div>
                    <div class="choice-overlay"></div>
                  </div>
                  <div class="choice-label-text">A1C to eAG</div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="form-field-layout with-label">
          <div class="field-label">Unit</div>

          <div class="form-field-element">
            <div class="choice-container">
              <div role="radiogroup" class="choice-options">
                <label class="choice-option">
                  <input
                    type="radio"
                    name="target-unit"
                    class="choice-input"
                    value="mg_dl"
                    v-model="targetUnit"
                  />

                  <div class="choice-option-container">
                    <div class="choice-radio"></div>
                    <div class="choice-overlay"></div>
                  </div>
                  <div class="choice-label-text">mg/dL</div>
                </label>

                <label class="choice-option">
                  <input
                    type="radio"
                    name="target-unit"
                    class="choice-input"
                    value="mmol_l"
                    v-model="targetUnit"
                  />

                  <div class="choice-option-container">
                    <div class="choice-radio"></div>
                    <div class="choice-overlay"></div>
                  </div>
                  <div class="choice-label-text">mmol/L</div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button class="save-calculation" type="submit" :disabled="loginRequired || saving || !!inputError">
        Save
      </button>
    </form>

    <DetailedResult
      v-if="!inputError"
      :inputValue="inputValue"
      :conversionType="conversionType"
      :targetUnit="targetUnit"
      :calculatedResult="calculatedResult"
    />

    <InquiryList :calcHistory="calcHistory" />
  </div>
</template>

<style scoped>
form.form-container .conversion-options {
  margin-top: 24px;
}

form.form-container .conversion-options .form-field-layout {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 13px;
  line-height: 18px;
}

form.form-container .conversion-options .field-label {
  flex: 0 0 78px;
  margin-bottom: 0;
}

form.form-container .conversion-options .choice-options {
  display: flex;
  align-items: center;
  gap: 12px;
}

form.form-container .conversion-options .choice-option {
  width: auto;
  margin-bottom: 0;
  flex-wrap: nowrap;
  white-space: nowrap;
  align-items: center;
}

dialog.login-required {
  text-align: center;
  box-sizing: border-box;
  width: min(440px, calc(100% - 32px));
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  margin: auto;
  padding: 40px 24px 24px;
  border: 1px solid #17405D;
  border-radius: 8px;
  background: #f4f8fb;
  color: #17405D;
}

.login-required::backdrop {
  background: rgba(0, 0, 0, 0.5);
}

.login-required h2 {
  margin: -40px -24px 20px;
  padding: 16px 48px;
  background: #17405D;
  color: #fff;
  font-size: 24px;
}

.login-required :focus-visible {
  outline: 3px solid #17405D;
  outline-offset: 3px;
}

.login-required p {
  margin: 0 0 12px;
  font-weight: 700;
}

.google-sign-in-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  max-width: 100%;
  border-radius: 4px;
}

.google-sign-in-link img {
  display: block;
  width: 175px;
  max-width: 100%;
  height: auto;
}

.save-calculation {
  margin-top: 8px;
  min-height: 44px;
  padding: 8px 20px;
  border: 0;
  border-radius: 8px;
  background: #17405D;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.save-calculation:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
